import {
  ArrowRight,
  CheckCircle2,
  Download,
  HelpCircle,
  Lightbulb,
  Rocket,
  Sparkles
} from 'lucide-react'
import SEO from '../SEO'
import DownloadButtons from './DownloadButtons'
import { Link } from 'react-router-dom'

function OpenClawLandingPage({
  seo,
  theme = 'cyan',
  source,
  productName = 'OpenClaw',
  showFriendLinks = true,
  downloadContent = null,
  hero,
  highlights = [],
  steps = [],
  faq = [],
  sections = [],
  screenshotCaption,
  closing
}) {
  const themeClass = {
    cyan: {
      shell: 'from-[#fff8f7] via-[#fffdfc] to-[#f7fbff]',
      glow: 'bg-cyan-400/18',
      chip: 'border-cyan-100 bg-cyan-50 text-cyan-700',
      navButton: 'text-slate-700 hover:bg-white/70 hover:text-slate-950',
      heroPanel:
        'border-white/80 bg-gradient-to-br from-white/75 via-white/60 to-cyan-50/70 shadow-[0_24px_70px_-52px_rgba(15,23,42,0.25)] backdrop-blur',
      softButton: 'border-cyan-100 bg-white text-slate-700 hover:border-cyan-200 hover:bg-cyan-50',
      primaryButton: 'bg-[#ff5a5f] text-white shadow-[0_24px_50px_-24px_rgba(255,90,95,0.85)] hover:bg-[#f54a50]',
      card: 'border-white bg-white/90 shadow-[0_32px_90px_-46px_rgba(15,23,42,0.28)]',
      subCard: 'border-slate-100 bg-white shadow-[0_22px_60px_-42px_rgba(15,23,42,0.18)]',
      title: 'text-cyan-950',
      body: 'text-slate-600',
      muted: 'text-slate-500',
      bullet: 'text-cyan-500'
    },
    emerald: {
      shell: 'from-[#f8fffb] via-[#fffdf9] to-[#f4faf8]',
      glow: 'bg-emerald-400/18',
      chip: 'border-emerald-100 bg-emerald-50 text-emerald-700',
      navButton: 'text-slate-700 hover:bg-white/70 hover:text-slate-950',
      heroPanel:
        'border-white/80 bg-gradient-to-br from-white/75 via-white/60 to-emerald-50/70 shadow-[0_24px_70px_-52px_rgba(15,23,42,0.25)] backdrop-blur',
      softButton: 'border-emerald-100 bg-white text-slate-700 hover:border-emerald-200 hover:bg-emerald-50',
      primaryButton: 'bg-[#ff6b57] text-white shadow-[0_24px_50px_-24px_rgba(255,107,87,0.8)] hover:bg-[#f65b47]',
      card: 'border-white bg-white/92 shadow-[0_32px_90px_-46px_rgba(15,23,42,0.28)]',
      subCard: 'border-slate-100 bg-white shadow-[0_22px_60px_-42px_rgba(15,23,42,0.18)]',
      title: 'text-emerald-950',
      body: 'text-slate-600',
      muted: 'text-slate-500',
      bullet: 'text-emerald-500'
    },
    violet: {
      shell: 'from-[#fff9ff] via-[#fffdfd] to-[#f7f8ff]',
      glow: 'bg-violet-400/18',
      chip: 'border-violet-100 bg-violet-50 text-violet-700',
      navButton: 'text-slate-700 hover:bg-white/70 hover:text-slate-950',
      heroPanel:
        'border-white/80 bg-gradient-to-br from-white/75 via-white/60 to-violet-50/70 shadow-[0_24px_70px_-52px_rgba(15,23,42,0.25)] backdrop-blur',
      softButton: 'border-violet-100 bg-white text-slate-700 hover:border-violet-200 hover:bg-violet-50',
      primaryButton: 'bg-[#ff5967] text-white shadow-[0_24px_50px_-24px_rgba(255,89,103,0.8)] hover:bg-[#f14b59]',
      card: 'border-white bg-white/92 shadow-[0_32px_90px_-46px_rgba(15,23,42,0.28)]',
      subCard: 'border-slate-100 bg-white shadow-[0_22px_60px_-42px_rgba(15,23,42,0.18)]',
      title: 'text-violet-950',
      body: 'text-slate-600',
      muted: 'text-slate-500',
      bullet: 'text-violet-500'
    },
    amber: {
      shell: 'from-[#fffaf6] via-[#fffdfb] to-[#fff8f0]',
      glow: 'bg-amber-400/18',
      chip: 'border-amber-100 bg-amber-50 text-amber-700',
      navButton: 'text-slate-700 hover:bg-white/70 hover:text-slate-950',
      heroPanel:
        'border-white/80 bg-gradient-to-br from-white/75 via-white/60 to-amber-50/70 shadow-[0_24px_70px_-52px_rgba(15,23,42,0.25)] backdrop-blur',
      softButton: 'border-amber-100 bg-white text-slate-700 hover:border-amber-200 hover:bg-amber-50',
      primaryButton: 'bg-[#ff6a4c] text-white shadow-[0_24px_50px_-24px_rgba(255,106,76,0.82)] hover:bg-[#f65a3d]',
      card: 'border-white bg-white/92 shadow-[0_32px_90px_-46px_rgba(15,23,42,0.28)]',
      subCard: 'border-slate-100 bg-white shadow-[0_22px_60px_-42px_rgba(15,23,42,0.18)]',
      title: 'text-amber-950',
      body: 'text-slate-600',
      muted: 'text-slate-500',
      bullet: 'text-amber-500'
    },
    rose: {
      shell: 'from-[#fff5f5] via-[#fffdfc] to-[#fff1f2]',
      glow: 'bg-rose-400/18',
      chip: 'border-rose-100 bg-rose-50 text-rose-600',
      navButton: 'text-slate-700 hover:bg-white/70 hover:text-slate-950',
      heroPanel:
        'border-white/80 bg-gradient-to-br from-white/75 via-white/60 to-rose-50/70 shadow-[0_24px_70px_-52px_rgba(15,23,42,0.25)] backdrop-blur',
      softButton: 'border-rose-100 bg-white text-slate-700 hover:border-rose-200 hover:bg-rose-50',
      primaryButton: 'bg-[#ff4d4f] text-white shadow-[0_24px_50px_-24px_rgba(255,77,79,0.85)] hover:bg-[#f03b3d]',
      card: 'border-white bg-white/92 shadow-[0_32px_90px_-46px_rgba(15,23,42,0.28)]',
      subCard: 'border-slate-100 bg-white shadow-[0_22px_60px_-42px_rgba(15,23,42,0.18)]',
      title: 'text-rose-950',
      body: 'text-slate-600',
      muted: 'text-slate-500',
      bullet: 'text-rose-500'
    }
  }[theme]

  const canonical = `${window.location.origin}${window.location.pathname}`
  const screenshot = 'https://www.dianclaw.cn/uploads/images/app-screenshot.png'
  const icpNo = import.meta.env.VITE_ICP_NO || '浙ICP备2023036369号'
  const companyName = import.meta.env.VITE_COMPANY_NAME || '杭州点壹下通讯科技有限公司'
  const phone = import.meta.env.VITE_CONTACT_PHONE || '0571-56111111'
  const address = import.meta.env.VITE_COMPANY_ADDRESS || '杭州市滨江区滨安路1197号7幢207室'
  const hideTermsLink = import.meta.env.VITE_HIDE_TERMS_LINK === 'true'
  const titleLines =
    typeof hero?.title === 'string'
      ? hero.title
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
      : []
  const descriptionParagraphs =
    typeof hero?.description === 'string'
      ? hero.description
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
      : []
  const hasFaq = Array.isArray(faq) && faq.length > 0

  return (
    <div className={`min-h-screen bg-gradient-to-br ${themeClass.shell} text-slate-900`}>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        disablePromoSuffix={seo.disablePromoSuffix}
        canonical={canonical}
        ogImage={screenshot}
      />

      <section className="relative overflow-hidden">
        <div className={`absolute left-10 top-16 h-48 w-48 rounded-full blur-3xl ${themeClass.glow}`} />
        <div className={`absolute right-10 top-40 h-64 w-64 rounded-full blur-3xl ${themeClass.glow}`} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.7),transparent_44%)]" />
        <div className="relative mx-auto max-w-7xl px-4 pt-8 pb-18 sm:px-6 sm:pt-10 sm:pb-24 lg:px-8 lg:pt-12 lg:pb-32">
          <div className="mb-12 flex items-center justify-between gap-4">
            <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${themeClass.chip}`}>
              <Sparkles className="h-4 w-4" />
              {productName} 专题页面
            </div>

            <nav className="hidden items-center justify-center gap-2 lg:flex">
              <a href="#features" className={`rounded-full px-4 py-2 text-sm font-medium transition ${themeClass.navButton}`}>
                核心功能
              </a>
              <a href="#guide" className={`rounded-full px-4 py-2 text-sm font-medium transition ${themeClass.navButton}`}>
                使用指南
              </a>
              {hasFaq ? (
                <a href="#faq" className={`rounded-full px-4 py-2 text-sm font-medium transition ${themeClass.navButton}`}>
                  常见问题
                </a>
              ) : null}
              <a href="#download" className={`rounded-full px-4 py-2 text-sm font-medium transition ${themeClass.navButton}`}>
                立即下载
              </a>
            </nav>

            <a
              href="#download"
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${themeClass.primaryButton}`}
            >
              <Download className="h-4 w-4" />
              立即下载
            </a>
          </div>

          <div className="mx-auto max-w-5xl pt-2 text-center">
            <div className={`mx-auto mb-7 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${themeClass.chip}`}>
              <Sparkles className="h-4 w-4" />
              {hero.eyebrow}
            </div>
            <h1 className={`mx-auto max-w-4xl text-4xl font-bold leading-[1.12] tracking-[-0.03em] sm:text-6xl lg:text-[4.4rem] ${themeClass.title}`}>
              {titleLines.length > 0
                ? titleLines.map((line, index) => (
                    <span key={`${line}-${index}`} className="block">
                      {line}
                    </span>
                  ))
                : hero.title}
            </h1>
            {hero.subtitle ? (
              <p className="mx-auto mt-6 max-w-3xl text-xl font-semibold leading-9 text-slate-900/85 sm:text-[1.55rem]">
                {hero.subtitle}
              </p>
            ) : null}
            <div className={`mx-auto mt-8 max-w-3xl rounded-[1.75rem] border px-7 py-6 text-center sm:px-8 ${themeClass.heroPanel}`}>
              <div className={`space-y-4 text-base leading-8 sm:text-[1.06rem] ${themeClass.body}`}>
                {descriptionParagraphs.length > 0 ? (
                  descriptionParagraphs.map((paragraph, index) => <p key={`${paragraph}-${index}`}>{paragraph}</p>)
                ) : (
                  <p>{hero.description}</p>
                )}
              </div>
            </div>
            <div className="mx-auto mt-8 flex max-w-4xl flex-wrap justify-center gap-3">
              {(hero?.tags || []).map((tag) => (
                <span
                  key={tag}
                  className={`rounded-full border px-4 py-2 text-sm ${themeClass.chip}`}
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="mx-auto mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="#download"
                className={`inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold transition ${themeClass.primaryButton}`}
              >
                <Download className="h-4 w-4" />
                {`立即下载 ${productName}`}
              </a>
              <a
                href="#guide"
                className={`inline-flex items-center gap-2 rounded-full border px-7 py-4 text-base font-semibold transition ${themeClass.softButton}`}
              >
                查看上手内容
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="mx-auto mt-12 max-w-5xl sm:mt-14 lg:mt-16">
            <div className={`relative overflow-hidden rounded-[2rem] border p-5 ${themeClass.card}`}>
              <div className={`mb-4 flex items-center justify-center gap-2 text-xs ${themeClass.muted}`}>
                <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                <span className="ml-3">{`${productName} Desktop Preview`}</span>
              </div>
              <img
                src={screenshot}
                alt={`${productName} 客户端界面截图`}
                className="w-full rounded-[1.5rem] border border-slate-100 object-cover"
              />
              <p className={`mt-4 text-sm leading-7 ${themeClass.muted}`}>
                {screenshotCaption}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="border-y border-slate-100 bg-white/60 backdrop-blur">
        <div className="mx-auto grid max-w-7xl gap-5 px-4 py-10 sm:px-6 lg:grid-cols-3 lg:px-8">
          {highlights.map((item) => {
            const Icon = item.icon || Lightbulb
            return (
              <div key={item.title} className={`rounded-3xl border p-7 ${themeClass.subCard}`}>
                <div className={`mb-4 inline-flex rounded-2xl p-3 ${themeClass.chip}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className={`text-xl font-semibold ${themeClass.title}`}>{item.title}</h2>
                <p className={`mt-3 text-sm leading-8 ${themeClass.body}`}>{item.description}</p>
              </div>
            )
          })}
        </div>
      </section>

      <section id="guide" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr]">
          <div className={`rounded-[2rem] border p-8 ${themeClass.subCard}`}>
            <div className={`mb-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${themeClass.chip}`}>
              <Rocket className="h-4 w-4" />
              快速上手
            </div>
            <h2 className={`text-3xl font-semibold ${themeClass.title}`}>{hero.guideTitle}</h2>
            <p className={`mt-4 text-base leading-8 ${themeClass.body}`}>{hero.guideDescription}</p>
            <ul className={`mt-6 space-y-3 text-sm leading-8 ${themeClass.body}`}>
              {hero.guideBullets.map((bullet) => (
                <li key={bullet} className="flex gap-3">
                  <CheckCircle2 className={`mt-1 h-4 w-4 shrink-0 ${themeClass.bullet}`} />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-5">
            {steps.map((step, index) => (
              <div key={step.title} className={`rounded-[2rem] border p-6 ${themeClass.subCard}`}>
                <div className="flex items-start gap-4">
                  <div className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border text-lg font-semibold ${themeClass.chip}`}>
                    {index + 1}
                  </div>
                  <div>
                    <h3 className={`text-xl font-semibold ${themeClass.title}`}>{step.title}</h3>
                    <p className={`mt-3 text-sm leading-8 ${themeClass.body}`}>{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="more" className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-2">
          {sections.map((section) => (
            <div key={section.title} className={`rounded-[2rem] border p-8 ${themeClass.subCard}`}>
              <h2 className={`text-2xl font-semibold ${themeClass.title}`}>{section.title}</h2>
              <p className={`mt-4 text-base leading-8 ${themeClass.body}`}>{section.description}</p>
              <ul className={`mt-6 space-y-3 text-sm leading-8 ${themeClass.body}`}>
                {section.items.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className={`mt-2 h-2 w-2 rounded-full ${themeClass.bullet.replace('text-', 'bg-')}`} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {hasFaq ? (
        <section id="faq" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className={`rounded-[2rem] border p-8 ${themeClass.subCard}`}>
            <div className={`mb-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${themeClass.chip}`}>
              <HelpCircle className="h-4 w-4" />
              常见问题
            </div>
            <div className="grid gap-5 lg:grid-cols-2">
              {faq.map((item) => (
                <div key={item.q} className={`rounded-3xl border p-6 ${themeClass.card}`}>
                  <h3 className={`text-lg font-semibold ${themeClass.title}`}>{item.q}</h3>
                  <p className={`mt-3 text-sm leading-8 ${themeClass.body}`}>{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section id="download" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className={`rounded-[2rem] border p-8 ${themeClass.subCard}`}>
          <div className={`mb-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${themeClass.chip}`}>
            <Download className="h-4 w-4" />
            {`下载 ${productName}`}
          </div>
          <h2 className={`text-3xl font-semibold ${themeClass.title}`}>{closing.title}</h2>
          <p className={`mt-4 max-w-3xl text-base leading-8 ${themeClass.body}`}>{closing.description}</p>
          <div className="mt-8">
            {downloadContent || <DownloadButtons theme={theme} source={source} />}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className={`rounded-[2rem] border p-8 text-center ${themeClass.subCard}`}>
          <h2 className={`text-3xl font-semibold ${themeClass.title}`}>{closing.footerTitle}</h2>
          <p className={`mx-auto mt-4 max-w-3xl text-base leading-8 ${themeClass.body}`}>
            {closing.footerDescription}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {closing.footerTags.map((tag) => (
              <span key={tag} className={`rounded-full border px-4 py-2 text-sm ${themeClass.chip}`}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-10 text-sm text-slate-500 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>备案号：{icpNo}</span>
              {!hideTermsLink ? (
                <>
                  <span className="hidden text-slate-500 md:inline">|</span>
                  <Link to="/terms" className="transition hover:text-slate-900">
                    服务协议
                  </Link>
                </>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-slate-500">
              <span>{companyName}</span>
              <span>{phone}</span>
              <span>{address}</span>
            </div>
          </div>
          {showFriendLinks ? (
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-slate-500">
              <span>友链</span>
              <span className="text-slate-300">|</span>
              <a
                href="https://soft.china.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-slate-900"
              >
                中华网软件
              </a>
            </div>
          ) : null}
        </div>
      </footer>
    </div>
  )
}

export default OpenClawLandingPage
